import { Formik, useField } from 'formik';
import {
  CompanyPageCompanyFragment,
  useCompanyPageUpdateCompanyMutation,
} from 'generated/graphql';
import {
  enableDisableRadioButtonOptions,
  EnableDisableValue,
} from 'views/Company/CompanyView/CompanyView.helpers';
import {
  EditFormWrapper,
  FormikRadioButtonGroup,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import {
  SPOTTED_ZEBRA_SCORE_DATA_TEST_ID,
  SPOTTED_ZEBRA_SCORE_HELP_TEXT,
  SPOTTED_ZEBRA_SCORE_LABEL,
} from './SpottedZebraScoreSection.constants';
import styles from './SpottedZebraScoreSection.module.scss';

type SpottedZebraScoreSectionFormValues = {
  spottedZebraScore: string;
};

type SpottedZebraScoreSectionFormProps = {
  company: CompanyPageCompanyFragment;
  onCancelClick: () => void;
  onSave: () => void;
};

const SpottedZebraScoreSectionForm = ({
  company,
  onCancelClick,
  onSave,
}: SpottedZebraScoreSectionFormProps) => {
  const { handleMsgType } = useNotification();
  const [editCompanyMutation] = useCompanyPageUpdateCompanyMutation();

  const handleSave = (values: SpottedZebraScoreSectionFormValues) => {
    editCompanyMutation({
      variables: {
        id: company.id,
        name: company.name,
        isSpottedZebraScoreEnabled:
          values.spottedZebraScore === EnableDisableValue.Enabled,
      },
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          title: 'Assessment score setting updated.',
          dismissable: true,
        });
        onSave();
      },
      onError: () => {
        handleMsgType({
          type: TNotification.error,
          title: 'Assessment score setting could not be updated',
          message: 'Something went wrong. Please try again.',
          dismissable: true,
        });
      },
    });
  };

  return (
    <Formik<SpottedZebraScoreSectionFormValues>
      initialValues={{
        spottedZebraScore: company.isSpottedZebraScoreEnabled
          ? EnableDisableValue.Enabled
          : EnableDisableValue.Disabled,
      }}
      onSubmit={handleSave}
    >
      {({ submitForm }) => (
        <EditFormWrapper
          onCancelClick={onCancelClick}
          onSaveClick={submitForm}
          title={SPOTTED_ZEBRA_SCORE_LABEL}
          formId="spotted-zebra-score-form"
          hasResetAllButton={false}
          data-testid={SPOTTED_ZEBRA_SCORE_DATA_TEST_ID}
        >
          <form
            onSubmit={event => event.preventDefault()}
            id="spotted-zebra-score-form-element"
            className={styles.form}
          >
            <FormikRadioButtonGroup
              id="spottedZebraScore"
              name="spottedZebraScore"
              options={enableDisableRadioButtonOptions}
              layout="horizontal"
              label={SPOTTED_ZEBRA_SCORE_LABEL}
              useFormikField={useField}
              className={styles.radioGroup}
            />
            <p className={styles.helpText}>{SPOTTED_ZEBRA_SCORE_HELP_TEXT}</p>
          </form>
        </EditFormWrapper>
      )}
    </Formik>
  );
};

export default SpottedZebraScoreSectionForm;
